import React, { useState, useMemo, useEffect } from 'react';
import {
  Comment,
  //  Info
} from '@mui/icons-material';
import { Box, Typography, Stack, IconButton, useTheme, Divider } from '@mui/material';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

import {
  GridContainer,
  GridItem,
  // Button,
  // ControlledTooltips,
  InformationField,
  Modal,
} from '../../../components/shared';
import ChildField from './ChildField';
import ReportMachineModelBody from './ReportMachineModelBody';
import { METER_TYPE_FILED_MAP } from '../../../constants/MeterTypeFieldMap';
import { MESSAGE } from '../../../constants/message';
import useStyles from '../style';
import IntercardFields from './IntercardFields';
import { getMeterLabel, getVisibleCollectionAssetMeters, sortAssetMeters } from '../../../utils/collection-methods';
import { CommentBox } from '../../../components/CommentBox';
import { checkObjectNotEmpty } from '../../../utils/common-methods';

const MachineFormAccordionContent = ({ asset }) => {
  const visibleAssetMeters = useMemo(() => getVisibleCollectionAssetMeters(asset?.assetMeters || []), [asset]);
  const { setFieldValue, values } = useFormikContext();

  const theme = useTheme();
  const classes = useStyles();
  const { locationCurrency, intercardData } = useSelector((state) => state.newCollection);

  const [showReportModal, setShowReportModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [intercardTransactions, setIntercardTransactions] = useState(asset.intercardTransactions ?? []);
  const [report, setReport] = useState({});
  const [isIssueReported, setIsIssueReported] = useState(false);

  // Use fixed 9-column grid for consistent alignment
  const maxFieldCount = 9;

  // Sort meters: Cash first, Credit second, then remaining by field count descending
  const sortedAssetMeters = useMemo(() => {
    const meters = [...visibleAssetMeters];
    sortAssetMeters(meters);

    return meters.sort((a, b) => {
      const nameA = a.meter?.name;
      const nameB = b.meter?.name;

      // Cash always first, Credit always second
      if (nameA === 'Cash' && nameB !== 'Cash') return -1;
      if (nameB === 'Cash' && nameA !== 'Cash') return 1;
      if (nameA === 'Credit' && nameB !== 'Credit') return -1;
      if (nameB === 'Credit' && nameA !== 'Credit') return 1;

      const fieldsA = METER_TYPE_FILED_MAP[nameA]?.length || 0;
      const fieldsB = METER_TYPE_FILED_MAP[nameB]?.length || 0;

      if (fieldsB !== fieldsA) return fieldsB - fieldsA;
      return (nameA || '').localeCompare(nameB || '');
    });
  }, [visibleAssetMeters]);

  // const openReportHandler = () => {
  //   setShowReportModal(true);
  // };
  useEffect(() => {
    if (values) {
      const fieldName = `report_${asset.subLocationId}_${asset.id}`;
      const reportedValueFromFormikContext = values[fieldName];
      setIsIssueReported(reportedValueFromFormikContext?.status);
      if (checkObjectNotEmpty(reportedValueFromFormikContext) && reportedValueFromFormikContext?.issue) {
        setReport({
          assetId: asset?.id,
          assetName: asset?.name,
          issue: reportedValueFromFormikContext?.issue,
          reason: reportedValueFromFormikContext?.reason,
          status: reportedValueFromFormikContext?.status,
        });
      } else {
        setReport({});
      }
    }
  }, [showReportModal, asset]);

  useEffect(() => {
    if (intercardData[asset?.id]?.intercardData) {
      setIntercardTransactions(intercardData[asset?.id]?.intercardData);
    } else {
      setIntercardTransactions([]);
    }
  }, [intercardData]);

  const submitReportHandler = (v) => {
    setReport(v);
    setFieldValue(`report_${asset.subLocationId}_${asset.id}`, v);
    setShowReportModal(false);
  };
  const openCommentHandler = () => {
    setShowCommentModal(true);
  };
  const submitCommentHandler = (v) => {
    setFieldValue(`comment_${asset.subLocationId}_${asset.id}`, v);
  };

  const hasIntercardRows = Array.isArray(intercardTransactions) && intercardTransactions.length > 0;

  // Group meters: Cash and Credit always above intercard, then those with more fields than intercard, and the rest below
  const intercardFieldCount = 3;
  const metersAboveIntercard = sortedAssetMeters.filter(
    (assetMeter) =>
      assetMeter?.meter?.name === 'Cash' ||
      assetMeter?.meter?.name === 'Credit' ||
      (METER_TYPE_FILED_MAP[assetMeter?.meter?.name]?.length || 0) > intercardFieldCount,
  );
  const metersBelowOrEqualIntercard = sortedAssetMeters.filter(
    (assetMeter) =>
      assetMeter?.meter?.name !== 'Cash' &&
      assetMeter?.meter?.name !== 'Credit' &&
      (METER_TYPE_FILED_MAP[assetMeter?.meter?.name]?.length || 0) <= intercardFieldCount,
  );

  const renderMeterRows = (meters) =>
    meters?.map((assetMeter, index) => {
      const { meter } = assetMeter;
      const meterLabel = getMeterLabel(assetMeter, locationCurrency);

      return (
        <GridItem sm={12} md={12} px={0} pt={0.25} key={`${meter?.id || index}`}>
          <Box className={classes.compactMeterRow}>
            <Box className={classes.compactMeterLabelWrap}>
              <Typography variant="subtitle4" className={classes.compactMeterLabel}>
                {meterLabel}
              </Typography>
            </Box>
            <Box
              key={meter?.id}
              className={classes.compactMeterGrid}
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${maxFieldCount}, 1fr)`,
              }}
            >
              <ChildField
                assetId={asset?.id}
                assetMeterId={assetMeter?.id}
                assetMeter={assetMeter}
                sublocationId={asset?.subLocationId}
                meterName={meter?.name}
                meterId={meter?.id}
                fields={METER_TYPE_FILED_MAP[meter?.name]}
                reported={Object.keys(report).length > 0}
                isAddNewCollection
                isCompactView
                maxFieldCount={maxFieldCount}
              />
            </Box>
          </Box>
        </GridItem>
      );
    });

  return (
    <React.Fragment>
      {visibleAssetMeters && visibleAssetMeters.length == 0 && (
        <InformationField my={1} pl={2} variant="subtitle1" value={MESSAGE.COLLECTION_NO_METERS} />
      )}
      {(visibleAssetMeters && visibleAssetMeters.length > 0) || hasIntercardRows ? (
        <React.Fragment>
          <GridContainer pb={0} className={classes.compactMetersContainer}>
            {renderMeterRows(metersAboveIntercard)}
            {hasIntercardRows && (
              <IntercardFields
                intercardTransactions={intercardTransactions}
                assetId={asset?.id}
                sublocationId={asset?.subLocationId}
                maxFieldCount={maxFieldCount}
              />
            )}
            {renderMeterRows(metersBelowOrEqualIntercard)}
          </GridContainer>
          <Divider />
          <Stack px={0.5} my={1} direction="row" justifyContent={'flex-start'} alignItems="top">
            {/* <Box>
              {Object.keys(report).length > 0 || isIssueReported ? (
                <Box display="flex" alignItems="center" onClick={() => setShowReportModal(true)}>
                  <Box pt={1}>
                    <ControlledTooltips tooltipValue={MESSAGE.REPORT_MACHINE_INFO}>
                      <Info fontSize="small" color="warning" />
                    </ControlledTooltips>
                  </Box>
                  <Typography variant="p4" pl={1}>
                    {MESSAGE.RECONCILIATION_TOOLTIP_NAME}
                  </Typography>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  className={classes.reportMachineBtn}
                  onClick={openReportHandler}
                >
                  {MESSAGE.REPORT_MACHINE_BTN_NAME}
                </Button>
              )}
            </Box> */}
            <Box px={1} pb={0}>
              <IconButton
                className={classes.comment}
                onClick={openCommentHandler}
                sx={{
                  color:
                    showCommentModal ||
                    (values[`comment_${asset.subLocationId}_${asset.id}`] &&
                      values[`comment_${asset.subLocationId}_${asset.id}`]?.length > 0)
                      ? theme.palette.primary.main
                      : theme.palette.secondary.main,
                  padding: 1,
                }}
              >
                <Comment />
              </IconButton>
            </Box>
            <Box className={classes.commentBox}>
              {showCommentModal && (
                <CommentBox
                  handleClose={() => setShowCommentModal(false)}
                  handleSubmit={submitCommentHandler}
                  initialCommentData={values[`comment_${asset.subLocationId}_${asset.id}`]}
                  data={{
                    titleVariant: 'h3',
                    title: MESSAGE.RECONCILIATION_COMMENT_TITLE,
                    fieldName: `comment_${asset.subLocationId}_${asset.id}`,
                  }}
                />
              )}
            </Box>
          </Stack>

          <Modal open={showReportModal} title={MESSAGE.REPORT_MACHINE_BTN_NAME}>
            <ReportMachineModelBody
              assetId={asset?.id}
              assetName={asset?.title}
              asset={asset}
              reportedIssue={setIsIssueReported}
              onConfirm={(v) => {
                submitReportHandler(v);
              }}
              onClose={() => {
                setShowReportModal(false);
                setReport({});
              }}
              isAddNewCollection={true}
              isIssueReported={isIssueReported}
            />
          </Modal>
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export default MachineFormAccordionContent;
